import modalStyles from "../modals/styles/Modal.module.scss";
import FontAwesome from "./FontAwesome";
import { transformOrganizationType } from "../../utils/StringUtilities";
import NotificationManager from "../notifications/NotificationManager";
import ApiClient, { ApiError } from "../../backends/ApiClient";
import { runInAction } from "mobx";
import { useStores } from "../../stores/useStores";
import { Box, Button, Card, Flex, Text } from "@radix-ui/themes";

export async function togglePublished(organizationStore: any, modalStore: any) {
  try {
    const client = await ApiClient();
    await client.POST("/organizations/{id}/toggle_published/", {
      params: { path: { id: organizationStore.currentOrganization.id } },
      body: {
        published: organizationStore.currentOrganization.published,
      } as never,
    });

    const expandedFields: string[] = [];
    organizationStore.currentOrganization.json_data?.terracedGrid?.enabled
      ? expandedFields.push("sub_navigations")
      : expandedFields.push("microsite");
    if (
      organizationStore.navigation.filter(
        (nav: Navigation) =>
          nav.organization_id === organizationStore.currentOrganization.id,
      ).length > 0
    ) {
      expandedFields.push("title_organization.navigation");
    }

    const org = await (client.GET("/organizations/{id}/", {
      params: {
        path: { id: organizationStore.currentOrganization.id },
        query: { expand: expandedFields.join(",") },
      },
    }) as any);

    runInAction(() => {
      organizationStore.currentOrganization.published = org.published;
      organizationStore.currentOrganization.sub_navigations =
        org?.sub_navigations;
      organizationStore.currentOrganization.microsite = org?.microsite;
      if (org?.title_organization?.navigation) {
        organizationStore.navigation = org?.title_organization?.navigation;
      }
    });

    modalStore.removeTop();
  } catch (e) {
    if (e instanceof ApiError && e.data) {
      console.error(e);
      NotificationManager.error(
        (e.data as string[])[0],
        "Could not change the status of the page",
        5000,
      );
    } else if (e instanceof Error) {
      NotificationManager.error(e.message);
    }

    modalStore.removeTop();
  }
}

export function publishedToggleOnClick(
  organizationStore: any,
  modalStore: any,
) {
  const orgType = transformOrganizationType(
    organizationStore.currentOrganization.type,
  );
  const error = `Sorry, but we couldn't ${!organizationStore.currentOrganization.published ? "publish" : "unpublish"} the ${orgType}. Please try again later or contact an administrator for
                assistance.`;
  modalStore.addModal({
    type: "blockui",
    idleContent: (props) => (
      <div id="sb-fluxRestoreDialog" style={{ cursor: "default" }}>
        <h3 style={{ marginBottom: "1em", whiteSpace: "pre-line" }}>
          {organizationStore.currentOrganization.published
            ? `Are you sure you want to unpublish this page?\n
                        All descendant pages will become unpublished and links to these pages will be hidden.`
            : `Are you sure you want to publish this page?\n
                        All descendant pages will be published and links to these pages will be restored.`}
        </h3>
        <Flex justify={"center"} gapX={"2"}>
          <Button variant="outline" onClick={modalStore.removeTop}>
            Cancel
          </Button>
          <Button variant="soft" onClick={props.onConfirm}>
            {organizationStore.currentOrganization.published
              ? "Unpublish Page"
              : "Publish Page"}
          </Button>
        </Flex>
      </div>
    ),
    pendingContent: () => (
      <div id="sb-fluxRestoreDialogMessageRestoring">
        <h3>
          <FontAwesome
            name="fa-spinner"
            prefix="fas"
            spin
            style={{ marginRight: "0.5em" }}
          />
          {organizationStore.currentOrganization.published
            ? "Unpublishing page..."
            : "Publishing page..."}
        </h3>
      </div>
    ),
    failureContent: () => (
      <div id="sb-fluxRestoreDialogMessageRestoreFailed">
        <h3>{error}</h3>
      </div>
    ),
    onConfirm: () => togglePublished(organizationStore, modalStore),
    closeEffect: () => {
      document.body.classList.remove(modalStyles.modalOpen);
    },
  });
}

export function UnpublishedInfoBox({ unpublishedItemType, handleClick }) {
  return (
    <Flex justify="center" my="2">
      <Card variant="surface" size="1">
        <Flex direction="column" align="center" gap="2" px="1">
          <Flex align="center" gap="3" wrap="wrap" justify="center">
            <Box style={{ color: "var(--accent-9)" }}>
              <FontAwesome prefix="fas" name="fa-eye-slash" size="lg" />
            </Box>
            <Text size="3">
              {`This ${unpublishedItemType} is unpublished! Only editors and admins can view this content.`}
            </Text>
          </Flex>
          <Button size="2" onClick={handleClick}>
            Publish
          </Button>
        </Flex>
      </Card>
    </Flex>
  );
}

export function UnpublishedOrganizationInfoBox({
  unpublishedItemType,
}: {
  unpublishedItemType: string;
}) {
  const { organizationStore, modalStore } = useStores();

  return (
    <UnpublishedInfoBox
      unpublishedItemType={unpublishedItemType}
      handleClick={() => publishedToggleOnClick(organizationStore, modalStore)}
    />
  );
}
